import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";

export default function MappingPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [templateHit, setTemplateHit] = useState(null);
  const [saveTpl, setSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");

  useEffect(() => {
    api.get("/uploads").then((r) => {
      const found = r.data.find((u) => u.id === id);
      if (found) {
        setUpload(found);
        setMapping(found.suggested_mapping || {});
        setTemplateHit(found.template_hit || null);
      }
    });
  }, [id]);

  const TARGETS_INV = ["", "sku", "brand", "model", "tire_size", "category", "quantity_on_hand", "cost", "selling_price", "supplier", "location"];
  const TARGETS_SAL = ["", "transaction_date", "sku", "quantity_sold", "selling_price", "unit_cost", "discount", "location", "invoice_id"];
  const REQ_INV = ["sku", "quantity_on_hand"];
  const REQ_SAL = ["transaction_date", "sku", "quantity_sold"];

  const submit = async () => {
    if (!upload) return;
    const req = upload.kind === "inventory" ? REQ_INV : REQ_SAL;
    const mapped = Object.values(mapping);
    const missing = req.filter((r) => !mapped.includes(r));
    if (missing.length) { toast.error(`Missing required mappings: ${missing.join(", ")}`); return; }
    setBusy(true);
    try {
      const params = saveTpl && tplName.trim() ? { save_template_as: tplName.trim() } : {};
      const { data } = await api.post(`/uploads/${id}/mapping`, { upload_id: id, mapping }, { params });
      setWarnings(data.warnings || []);
      toast.success(`Applied mapping to ${data.row_count} rows.${saveTpl && tplName.trim() ? " Template saved." : ""}`);
      if ((data.warnings || []).length === 0) nav("/app");
    } catch (err) { toast.error(err?.response?.data?.detail || "Mapping failed"); }
    finally { setBusy(false); }
  };

  if (!upload) return <DashboardLayout><div className="text-sm text-[#3B424B]">Loading…</div></DashboardLayout>;

  const targets = upload.kind === "inventory" ? TARGETS_INV : TARGETS_SAL;
  const required = upload.kind === "inventory" ? REQ_INV : REQ_SAL;

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="mapping-heading">Map columns · {upload.filename}</h1>
      <p className="mt-1 text-sm text-[#3B424B]">Confirm which of your columns match TreadMargin fields. Required fields are marked with <span className="text-[#C9943D] font-semibold">*</span>.</p>

      {templateHit && (
        <div className="mt-4 tm-card p-4 bg-[var(--tm-off)] flex items-center gap-3" data-testid="mapping-template-hit">
          <div className="h-8 w-8 rounded-lg bg-[#14624A] text-white flex items-center justify-center text-xs font-bold">TPL</div>
          <div className="text-sm"><span className="font-semibold text-[#0F1115]">Saved template applied:</span> <span className="text-[#3B424B]">{templateHit.name}</span></div>
        </div>
      )}

      {upload.health && (
        <div className="mt-4 tm-card p-5 bg-white" data-testid="mapping-health">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Import health score</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="text-3xl font-bold font-mono" data-testid="mapping-health-score">{upload.health.score}</div>
                <div className="text-sm text-[#3B424B]">/ 100 · {upload.health.total_rows} rows</div>
              </div>
            </div>
            <div className="w-full sm:w-64 h-2 rounded-full bg-[var(--tm-off)] overflow-hidden">
              <div className="h-full" style={{ width: `${upload.health.score}%`, background: upload.health.score >= 80 ? "#14624A" : upload.health.score >= 50 ? "#C9943D" : "#D9381E" }} />
            </div>
          </div>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
            {(upload.health.checks || []).map((c) => (
              <li key={c.key} className="flex items-start gap-2" data-testid={`mapping-health-${c.key}`}>
                <span className={`mt-0.5 h-4 w-4 rounded-full inline-block ${c.ok ? "bg-[#14624A]" : "bg-[#D9381E]"}`} />
                <span className={c.ok ? "text-[#0F1115]" : "text-[#3B424B]"}>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 tm-card bg-white overflow-hidden">
        <div className="overflow-x-auto tm-scroll">
          <table className="w-full text-sm">
            <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]">
              <tr><th className="p-4">Your column</th><th className="p-4">TreadMargin field</th></tr>
            </thead>
            <tbody>
              {(upload.headers || []).map((h) => (
                <tr key={h} className="border-t border-[var(--tm-border)]">
                  <td className="p-4 font-mono text-[13px]">{h}</td>
                  <td className="p-4">
                    <select value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} className="tm-input h-10 max-w-xs" data-testid={`map-select-${h}`}>
                      {targets.map((t) => <option key={t} value={t}>{t ? `${t}${required.includes(t) ? " *" : ""}` : "— Skip —"}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="mt-6 tm-card p-5 bg-white" data-testid="mapping-warnings">
          <div className="flex items-center gap-2 font-semibold"><AlertCircle size={16} className="text-[#C9943D]" /> Validation warnings ({warnings.length})</div>
          <ul className="mt-3 text-sm space-y-1 text-[#3B424B] max-h-48 overflow-y-auto tm-scroll">{warnings.map((w, i) => <li key={i}>· {w}</li>)}</ul>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <button onClick={submit} disabled={busy} className="tm-btn-primary" data-testid="mapping-apply">{busy ? "Applying…" : "Apply mapping & save"}</button>
        <button onClick={() => nav("/app/upload")} className="tm-btn-outline" data-testid="mapping-cancel">Back</button>
        <label className="ml-2 inline-flex items-center gap-2 text-sm text-[#3B424B]" data-testid="mapping-save-tpl-toggle">
          <input type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} className="h-4 w-4 accent-[#14624A]" data-testid="mapping-save-tpl-checkbox" />
          Save this mapping as a template
        </label>
        {saveTpl && (
          <input placeholder="Template name (e.g. 'My POS export')" className="tm-input h-10 max-w-xs" value={tplName} onChange={(e) => setTplName(e.target.value)} data-testid="mapping-save-tpl-name" />
        )}
      </div>
    </DashboardLayout>
  );
}
